import { useEffect, useRef, useCallback, useState } from "react";
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import config from "../config";

const useSupportWebSocket = ({
  threadUuid,
  userId,
  onMessageReceived,
  onTypingReceived,
}) => {
  const clientRef = useRef(null);
  const subscriptionRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  const disconnect = useCallback(() => {
    setIsConnecting(false);

    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }

    if (clientRef.current) {
      if (clientRef.current.connected) {
        clientRef.current.deactivate();
      }
      clientRef.current = null;
    }

    setIsConnected(false);
    reconnectAttemptsRef.current = 0;
  }, []);

  const connect = useCallback(() => {
    if (!threadUuid || !userId) return;

    // Prevent multiple connections
    if (
      isConnecting ||
      (clientRef.current && clientRef.current.connected) ||
      isConnected
    ) {
      return;
    }

    setIsConnecting(true);

    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Disconnect existing client if any
    if (clientRef.current) {
      clientRef.current.deactivate();
    }

    const client = new Client({
      webSocketFactory: () =>
        new SockJS(`${config.JAVA_API_BASE_URL}ws/support-chat`),
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      debug: (str) => {},
      onConnect: () => {
        setIsConnected(true);
        setIsConnecting(false);
        // Reset reconnect attempts on successful connection
        reconnectAttemptsRef.current = 0;

        // Subscribe to messages for this thread and user
        if (client && client.connected) {
          subscriptionRef.current = client.subscribe(
            `/queue/thread/${threadUuid}/user/${userId}`,
            (message) => {
              try {
                const data = JSON.parse(message.body);
 
                if (data.type === "TYPING") {
                  // Typing indicator message
                  if (onTypingReceived) {
                    onTypingReceived({
                      type: data.type,
                      threadUuid: data.threadUuid,
                      userId: data.userId,
                      userName: data.userName,
                      isTyping: data.isTyping,
                    });
                  }
                } else if (data.type === "ERROR") { 

                  // Handle specific error types
                  if (
                    data.message &&
                    data.message.includes("Sender not found")
                  ) { 
                    // You could emit a custom event here to notify the UI
                    if (typeof window !== "undefined") {
                      window.dispatchEvent(
                        new CustomEvent("websocket-auth-error", {
                          detail: { message: data.message, userId, threadUuid },
                        }),
                      );
                    }
                  }
 
                  return;
                } else if (data.type === "NOTIFICATION") {
                  return;
                } else if (
                  data.id &&
                  (data.message || data.attachmentUrl) &&
                  !data.type
                ) {
                  if (onMessageReceived) { 
                    // Use attachmentUrl from server response directly
                    let messageText = data.message || "";
                    let attachmentUrl = data.attachmentUrl || null;
                    let messageType = "TEXT";
                    let hasCaption = false;

                    // Determine message type based on attachmentUrl
                    if (attachmentUrl) {
                      const isVideo = /\.(mp4|webm|ogg|mov|avi)$/i.test(attachmentUrl);
                      if (messageText && messageText !== "📎 Attachment") {
                        messageType = "MIXED"; // Both media and caption
                        hasCaption = true;
                      } else {
                        messageType = isVideo ? "VIDEO" : "IMAGE"; // Media only
                        hasCaption = false;
                      }
                      // If message is just the default attachment text, clear it
                      if (messageText === "📎 Attachment") {
                        messageText = "";
                      }
                    }

                    onMessageReceived({
                      id: data.id,
                      threadUuid: data.threadUuid || threadUuid,
                      senderId: data.senderId,
                      senderType: data.senderType,
                      message: messageText,
                      messageType: messageType,
                      attachmentUrl: attachmentUrl,
                      createdAt:
                        data.timestamp ||
                        data.createdAt ||
                        new Date().toISOString(),
                      timestamp: data.timestamp,
                      senderDetails: null,
                      hasCaption: hasCaption,
                    });
                  }
                } else {
                }
              } catch (error) {
                console.error("Error parsing WebSocket message");
              }
            },
          );
        }
      },
      onDisconnect: () => {
        setIsConnected(false);
        setIsConnecting(false);

        // Clear any existing timeout before setting new one
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }

        // Only attempt reconnection if we haven't exceeded max attempts
        if (
          threadUuid &&
          userId &&
          reconnectAttemptsRef.current < maxReconnectAttempts
        ) {
          reconnectAttemptsRef.current += 1;
          const delay = Math.min(5000 * reconnectAttemptsRef.current, 30000);

          reconnectTimeoutRef.current = setTimeout(() => {
            if (
              threadUuid &&
              userId &&
              clientRef.current &&
              !clientRef.current.connected
            ) {
              connect();
            }
          }, delay);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          console.error(
            "Max reconnection attempts reached. Stopping reconnection.",
          );
        }
      },
      onStompError: (frame) => {
        setIsConnected(false);
        setIsConnecting(false);
      },
      onWebSocketError: (event) => {
        setIsConnected(false);
        setIsConnecting(false);
      },
    });

    client.activate();
    clientRef.current = client;
  }, [
    threadUuid,
    userId,
    onMessageReceived,
    onTypingReceived,
    isConnected,
    isConnecting,
  ]);

  const sendMessage = useCallback(
    (message, senderType = "SUPPORT", attachmentUrl = null, messageType = "TEXT") => {
      if (!clientRef.current || !clientRef.current.connected || !isConnected) {
        console.error("WebSocket not connected");
        return Promise.reject(new Error("WebSocket not connected"));
      }

      if (!message.trim() && !attachmentUrl) {
        return Promise.reject(new Error("Message cannot be empty"));
      }

      if (!threadUuid || !userId) {
        console.error("Missing required parameters");
        return Promise.reject(new Error("Missing threadUuid or userId"));
      }

      return new Promise((resolve, reject) => {
        try {
          const messagePayload = {
            senderId: parseInt(userId, 10),
            message: message.trim() || "",
            senderType: senderType,
            attachmentUrl: attachmentUrl || null,
            messageType: messageType || "TEXT",
          }; 

          // Send message according to API spec
          clientRef.current.publish({
            destination: `/app/chat.send/${threadUuid}`,
            body: JSON.stringify(messagePayload),
          }); 

          // Add a small delay to ensure message is processed
          setTimeout(() => {
            resolve();
          }, 100);
        } catch (error) {
          console.error("Error sending message:", error);
          reject(error);
        }
      });
    },
    [threadUuid, userId, isConnected],
  );

  const sendTypingIndicator = useCallback(
    (isTyping, userName = "Support Agent") => {
      if (!clientRef.current || !clientRef.current.connected || !isConnected) {
        return;
      }

      if (!threadUuid || !userId) {
        return;
      }

      try {
        const typingPayload = {
          userId: parseInt(userId, 10), // Ensure it's a proper integer as per API spec
          userName: userName,
          isTyping: isTyping,
        };

        // Send typing indicator according to API spec
        clientRef.current.publish({
          destination: `/app/chat.typing/${threadUuid}`,
          body: JSON.stringify(typingPayload),
        });
      } catch (error) {
        console.error("Error sending typing indicator");
      }
    },
    [threadUuid, userId, isConnected],
  );

  useEffect(() => {
    // Only connect if we have required parameters and are not already connected/connecting
    if (threadUuid && userId && !isConnected && !isConnecting) {
      connect();
    }

    // Cleanup on unmount or when threadUuid/userId change
    return () => {
      if (isConnected || isConnecting) {
        disconnect();
      }
    };
  }, [threadUuid, userId]); // Only depend on the core parameters

  return {
    sendMessage,
    sendTypingIndicator,
    isConnected,
    isConnecting,
    connect: isConnected || isConnecting ? null : connect, // Only expose connect if not connected/connecting
    disconnect,
  };
};

export default useSupportWebSocket;
